/**
 * Typed Hono RPC client factory + Clerk-authenticated transport, shared by the
 * Chrome extension and the web SPA.
 *
 * Auth wiring: the Clerk session token is supplied via a mutable `tokenGetter`
 * (set by each app's React layer — `AuthBridge` — once Clerk is loaded). This
 * indirection keeps the module free of React/Clerk imports, so the same client
 * can also be used from background/content scripts if needed.
 *
 * The worker's `AppType` is threaded into `hc<>`, so every route, path param,
 * and JSON body is end-to-end type-checked against the backend router. Always
 * use `jobsApi` / `assessmentsApi` (or `api` directly) instead of hand-rolled
 * fetch calls.
 */
import { hc } from 'hono/client';
import type { AppType } from '@my-app/worker';
import type { AnswerValue, Dimension } from './types';

type TokenGetter = () => Promise<string | null>;

let tokenGetter: TokenGetter = async () => null;

/** Register the Clerk token source. Called once from the app's React root. */
export function setSessionTokenGetter(getter: TokenGetter): void {
  tokenGetter = getter;
}

/**
 * Authenticated transport: injects the Clerk session token into every request.
 * Typed exactly as the global `fetch` so `hc`'s overload resolves correctly.
 */
const authedFetch: typeof fetch = async (input, init) => {
  const token = await tokenGetter();
  if (token) {
    const headers = new Headers(init?.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return fetch(input, { ...init, headers });
  }
  return fetch(input, init);
};

/** Body shape for `POST /api/jobs` (create / upsert a saved job). */
export type CreateJobBody = {
  source?: string | null;
  sourceJobId?: string | null;
  title: string;
  advertiser?: string | null;
  url: string;
  description: string;
};

/**
 * Build the typed client + convenience helpers for a given worker origin.
 * Each app calls this with its own `API_BASE_URL` (from its `.env`), keeping
 * config local while sharing all transport/auth/helper logic.
 */
export function createApiClient(baseUrl: string) {
  const api = hc<AppType>(baseUrl, { fetch: authedFetch });

  const jobsApi = {
    list: () => api.api.jobs.$get(),
    get: (id: string) => api.api.jobs[':id'].$get({ param: { id } }),
    create: (body: CreateJobBody) => api.api.jobs.$post({ json: body }),
    setStarred: (id: string, starred: boolean) =>
      api.api.jobs[':id'].$patch({ param: { id }, json: { starred } }),
    remove: (id: string) => api.api.jobs[':id'].$delete({ param: { id } }),
  };

  /**
   * JD self-assessment helpers. The generate/take flow lives on
   * `/api/jobs/:jobId/breakdown`, `/api/jobs/:jobId/quiz`, and
   * `/api/quizzes/:quizId/assessments`.
   */
  const assessmentsApi = {
    // --- JD Breakdown (Stage 1: extract + validate) ---
    // Trigger (or re-trigger) a JD breakdown. Consumes 1 generation quota.
    generateBreakdown: (jobId: string) =>
      api.api.jobs[':jobId'].breakdown.$post({ param: { jobId } }),
    // Fetch the breakdown status + extraction (if ready). Polled while generating.
    getBreakdown: (jobId: string) => api.api.jobs[':jobId'].breakdown.$get({ param: { jobId } }),

    // --- Assessment quiz (Stage 2: question generation) ---
    // Generate (or regenerate) a quiz. Requires a ready breakdown. A full quiz
    // costs 1.0 generation; pass `dimension` to generate a single-dimension
    // practice quiz for just that funnel dimension (costs 0.5).
    generateQuiz: (jobId: string, opts?: { numQuestions?: number; dimension?: Dimension }) => {
      const body: { numQuestions?: number; dimension?: Dimension } = {};
      if (opts?.numQuestions) body.numQuestions = opts.numQuestions;
      if (opts?.dimension) body.dimension = opts.dimension;
      return api.api.jobs[':jobId'].quiz.$post({ param: { jobId }, json: body });
    },
    // Fetch the saved job's quiz (questions only — no expected answers).
    getQuiz: (jobId: string) => api.api.jobs[':jobId'].quiz.$get({ param: { jobId } }),
    // Submit answers and get a scored assessment back (with a per-question review).
    // The `idempotencyKey` is generated per attempt (NOT per request) so the
    // server collapses duplicate submits that share the same key into the
    // original attempt. Retries after a network failure MUST reuse the same key;
    // a fresh retake (after seeing results) MUST use a new one. The hook layer
    // (useJobAssessment) manages this.
    submitAssessment: (
      quizId: string,
      answers: Array<{ index: number; selected: AnswerValue }>,
      idempotencyKey: string,
    ) =>
      api.api.quizzes[':quizId'].assessments.$post({
        param: { quizId },
        json: { answers, idempotencyKey },
      }),
    // List ALL attempts for a job — across every quiz version. Replaces the
    // per-quiz listAssessments call so old attempts survive a regenerate.
    getAttempts: (jobId: string) => api.api.jobs[':jobId'].attempts.$get({ param: { jobId } }),
    // List past attempts for a single quiz version (used by the submit flow).
    listAssessments: (quizId: string) =>
      api.api.quizzes[':quizId'].assessments.$get({ param: { quizId } }),
    // Get one assessment with full review detail.
    getAssessment: (id: string) => api.api.assessments[':id'].$get({ param: { id } }),
    // Latest assessment per job (for list/detail score badges). One round-trip.
    getSummaries: () => api.api['assessment-summaries'].$get(),
  };

  return { api, jobsApi, assessmentsApi };
}
